import { formatISO } from 'date-fns';
import { Check, NotebookPen } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmEventDTO } from '../../../server/dto/crm-event-dto.ts';
import type { CrmNoteDTO } from '../../../server/dto/crm-note-dto.ts';

type Props = {
    event: CrmEventDTO & { notes: CrmNoteDTO[] };
    date: string;
    disableTooltips?: boolean;
    onClick: (e: MouseEvent<HTMLButtonElement>) => void;
};

type ConditionalTooltipProps = {
    disabled: boolean;
    content: ReactNode;
    children: ReactNode;
};

const ConditionalTooltip = ({ disabled, content, children }: ConditionalTooltipProps): ReactNode => {
    if (disabled) {
        return children;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent>{content}</TooltipContent>
        </Tooltip>
    );
};

export function EventLogButton({ event, date, onClick, disableTooltips }: Props): ReactNode {
    const { t } = useLocale('events');

    const isFutureDate = date > formatISO(new Date(), { representation: 'date' });

    if (isFutureDate) {
        return null;
    }

    return (
        <div className="w-full flex justify-end">
            <ConditionalTooltip
                disabled={!!event.customerId || !!disableTooltips}
                content={t('card_protocol__no_customer_tooltip')}
            >
                <span className="inline-block">
                    <Button
                        disabled={!event.customerId}
                        variant={event.notes.length > 0 ? 'ghost' : 'secondary'}
                        icon={event.notes.length > 0 ? Check : NotebookPen}
                        onClick={onClick}
                        className={event.notes.length > 0 ? 'text-green-600' : undefined}
                    >
                        {event.notes.length > 0 ? t('card_log_button_logged') : t('card_log_button')}
                    </Button>
                </span>
            </ConditionalTooltip>
        </div>
    );
}
